import type { FeedLog } from '../../types/production'
import { normalizeToIsoZ } from '../../utils/dateNormalization'
import {
  buildImportPreviewRecord,
  buildImportPreviewRows,
  buildImportSession,
  buildImportValidationResults,
  createImportDedupeIssue,
  createImportRowIssue,
  mapImportHeaders,
  parseCsvImportTable,
  type ImportMappedHeader,
  type ImportRecordResult,
  type ImportSourceFile,
  type ImportSession,
  type ParsedImportTable
} from '../../utils/importTable'
import { ensureUniqueImportedRowId, toStableImportIdString } from '../../utils/importRowIdentity'
import { findAvailableLotMatch, resolveCanonicalLotKey } from '../../utils/lotIdentity'
import { toDayKeyDateOnly } from '../../utils/productionFeedKgDerivation'

export type FeedImportLot = {
  id: string
  lotKey: string
  stage?: string
}

type FeedImportTargetKey = 'feed'
type FeedImportContext = {
  farmId: string
  productionUnitId: string
}
type FeedImportField = keyof FeedLog | 'lotId'
type FeedMappedHeader = ImportMappedHeader<FeedImportField>

export type FeedImportRowResult = ImportRecordResult<FeedLog, FeedImportField>
export type FeedImportSession = ImportSession<FeedLog, FeedImportField, FeedImportTargetKey, FeedImportContext>

// Strip accent marks before lowercasing so Spanish-locale headers like
// "Alimento", "Notas" normalize to their accent-less forms and match
// aliases without listing every diacritic variant (mirrors the Water
// importer's #46 fix).
const normalizeFeedImportHeader = (value: string): string => (
  value
    .toString()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
)

const FEED_IMPORT_FIELD_MAP: Record<string, FeedImportField> = {
  id: 'id',
  farmid: 'farmId',
  productionunitid: 'productionUnitId',
  productionunit: 'productionUnitId',
  unitid: 'productionUnitId',
  date: 'recordedAt',
  datetime: 'recordedAt',
  recordedat: 'recordedAt',
  timestamp: 'recordedAt',
  time: 'recordedAt',
  lot: 'lot',
  lotid: 'lotId',
  feed: 'feedKg',
  feedkg: 'feedKg',
  feedused: 'feedKg',
  feedamount: 'feedKg',
  feedquantity: 'feedKg',
  feedtype: 'feedType',
  feedname: 'feedType',
  type: 'feedType',
  sfr: 'sfrPercent',
  sfrpct: 'sfrPercent',
  sfrpercent: 'sfrPercent',
  biomass: 'biomassKg',
  biomasskg: 'biomassKg',
  fcr: 'fcr',
  notes: 'notes',
  note: 'notes',
  comment: 'notes',
  comments: 'notes',
  // Spanish-locale aliases.
  fecha: 'recordedAt',
  fechahora: 'recordedAt',
  lote: 'lot',
  loteid: 'lotId',
  alimento: 'feedKg',
  alimentokg: 'feedKg',
  kgalimento: 'feedKg',
  cantidadalimento: 'feedKg',
  tipo: 'feedType',
  tipodealimento: 'feedType',
  notas: 'notes',
  nota: 'notes',
  observaciones: 'notes',
  observacion: 'notes',
  comentarios: 'notes',
  comentario: 'notes'
}

const trimOptionalString = (value: unknown): string | undefined => {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return undefined
    return value.toString()
  }
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed || undefined
}

const normalizeImportedFeedDecimalString = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return undefined
    return value.toString()
  }
  if (typeof value !== 'string') return undefined

  const trimmed = value.trim()
  if (!trimmed) return undefined

  const parsed = Number.parseFloat(trimmed.replaceAll(',', '.'))
  if (!Number.isFinite(parsed)) return undefined
  return parsed.toString()
}

type ResolvedImportedLot = {
  lotKey?: string
  // Charter D-1: FeedLogService now rejects a create with no lotId. Only set
  // when resolved against a real Lot (an exact lotKeyById hit or a matched
  // AvailableLotMatch) — a free-text label or unrecognized id string cannot
  // be trusted as a UUID, so those cases leave lotId unset and surface as
  // a required-field error instead of silently shipping an unsyncable row.
  lotId?: string
}

const resolveImportedFeedLot = (args: {
  row: Record<string, unknown>
  availableLots: FeedImportLot[]
  lotKeyById: Map<string, string>
}): ResolvedImportedLot => {
  const incomingLotId = trimOptionalString(args.row.lotId)
  const incomingLot = trimOptionalString(args.row.lot)

  if (incomingLotId) {
    const mappedLot = args.lotKeyById.get(incomingLotId)
    if (mappedLot) return { lotKey: mappedLot, lotId: incomingLotId }

    const matchedFromId = findAvailableLotMatch(incomingLotId, args.availableLots)
    if (matchedFromId.status === 'matched') {
      return { lotKey: matchedFromId.match.lotKey, lotId: matchedFromId.match.id }
    }

    if (!incomingLot) return { lotKey: incomingLotId }
  }

  if (!incomingLot) return {}

  const matchedFromLabel = findAvailableLotMatch(incomingLot, args.availableLots)
  if (matchedFromLabel.status === 'matched') {
    return { lotKey: matchedFromLabel.match.lotKey, lotId: matchedFromLabel.match.id }
  }

  return { lotKey: incomingLot }
}

const pushInvalidDecimalIssue = (args: {
  issues: Array<ReturnType<typeof createImportRowIssue<FeedImportField>>>
  previewRow: Parameters<typeof createImportRowIssue<FeedImportField>>[0]['previewRow']
  targetField: Extract<FeedImportField, 'feedKg' | 'sfrPercent' | 'biomassKg' | 'fcr'>
  value: unknown
  codePrefix: string
  required?: boolean
}) => {
  const normalized = normalizeImportedFeedDecimalString(args.value)
  const rawValue = trimOptionalString(args.value)

  if (!rawValue) {
    if (args.required) {
      args.issues.push(createImportRowIssue({
        previewRow: args.previewRow,
        targetField: args.targetField,
        code: `${args.codePrefix}-required`,
        severity: 'error',
        message: 'A numeric value is required.'
      }))
    }
    return
  }

  if (!normalized) {
    args.issues.push(createImportRowIssue({
      previewRow: args.previewRow,
      targetField: args.targetField,
      code: `${args.codePrefix}-invalid`,
      severity: 'error',
      message: 'Value must be a recognizable number.'
    }))
    return
  }

  if (Number.parseFloat(normalized) < 0) {
    args.issues.push(createImportRowIssue({
      previewRow: args.previewRow,
      targetField: args.targetField,
      code: `${args.codePrefix}-non-negative`,
      severity: 'error',
      message: 'Value must be zero or greater.'
    }))
  }
}

const validateFeedImportRecord = (args: {
  previewRow: Parameters<typeof createImportRowIssue<FeedImportField>>[0]['previewRow']
  record: Record<string, unknown>
}) => {
  const issues: Array<ReturnType<typeof createImportRowIssue<FeedImportField>>> = []
  const rawRecordedAt = trimOptionalString(args.record.recordedAt)

  if (!rawRecordedAt) {
    issues.push(createImportRowIssue({
      previewRow: args.previewRow,
      targetField: 'recordedAt',
      code: 'feed-recorded-at-required',
      severity: 'error',
      message: 'Recorded at is required.'
    }))
  } else {
    const normalizedRecordedAt = normalizeToIsoZ(rawRecordedAt)
    if (!normalizedRecordedAt.ok) {
      issues.push(createImportRowIssue({
        previewRow: args.previewRow,
        targetField: 'recordedAt',
        code: 'feed-recorded-at-invalid',
        severity: 'error',
        message: 'Recorded at must be a recognizable date or date-time.'
      }))
    }
  }

  pushInvalidDecimalIssue({
    issues,
    previewRow: args.previewRow,
    targetField: 'feedKg',
    value: args.record.feedKg,
    codePrefix: 'feed-kg',
    required: true
  })
  pushInvalidDecimalIssue({
    issues,
    previewRow: args.previewRow,
    targetField: 'sfrPercent',
    value: args.record.sfrPercent,
    codePrefix: 'feed-sfr-percent'
  })
  pushInvalidDecimalIssue({
    issues,
    previewRow: args.previewRow,
    targetField: 'biomassKg',
    value: args.record.biomassKg,
    codePrefix: 'feed-biomass-kg'
  })
  pushInvalidDecimalIssue({
    issues,
    previewRow: args.previewRow,
    targetField: 'fcr',
    value: args.record.fcr,
    codePrefix: 'feed-fcr'
  })

  return issues
}

const buildFeedMappedHeaders = (table: ParsedImportTable): FeedMappedHeader[] => {
  return mapImportHeaders({
    headers: table.headers,
    normalizeHeader: normalizeFeedImportHeader,
    targetByNormalizedHeader: FEED_IMPORT_FIELD_MAP
  })
}

const buildFeedImportArtifactsFromCsvText = (args: {
  text: string
  existingRows: FeedLog[]
  availableLots: FeedImportLot[]
  lotKeyById: Map<string, string>
  assignedContext?: FeedImportContext | null
  /**
   * Farm (or browser) timezone used to derive the `(lot, day)` dedup key for
   * the `feed-duplicate-lot-day` warning — mirrors v2.1 #47/#41. Without it,
   * the legacy UTC-prefix day-key behavior is used.
   */
  timeZone?: string
}): {
  table: ParsedImportTable
  mappedHeaders: FeedMappedHeader[]
  results: FeedImportRowResult[]
} | null => {
  const parsedTable = parseCsvImportTable(args.text)
  if (!parsedTable) return null

  const mappedHeaders = buildFeedMappedHeaders(parsedTable)
  const previewRows = buildImportPreviewRows({
    table: parsedTable,
    mappedHeaders,
    normalizeCellValue: (value) => value.trim()
  })
  const existingIds = new Set(args.existingRows.map((row) => toStableImportIdString(row.id)))
  const importedIds = new Set<string>()
  const dayKeyOptions = args.timeZone ? { timeZone: args.timeZone } : undefined
  // Preventive (lot, day) dedup, same shape as v2.1 #41 (production) / #42
  // (water): a CSV with no stable id column produces a fresh id on every
  // import run, so re-uploading the same file silently creates duplicate
  // FeedLog rows with nothing at commit time telling the operator. Index
  // existing rows by `(dayKey, lotToken)` and warn — not block — when an
  // imported row collides with an existing row or another row in this batch.
  const existingLotDayKeys = new Set<string>()
  for (const row of args.existingRows) {
    if (!row) continue
    const lotToken = resolveCanonicalLotKey(row.lotId, row.lot)
    if (lotToken === 'unknown-lot') continue
    const dayKey = toDayKeyDateOnly(row.recordedAt ?? '', dayKeyOptions)
    if (!dayKey) continue
    existingLotDayKeys.add(`${dayKey}::${lotToken}`)
  }
  const importedLotDayKeys = new Set<string>()

  const results = buildImportValidationResults({
    previewRows,
    buildRecord: (previewRow) => buildImportPreviewRecord({ previewRow }),
    validateRecord: ({ previewRow, record }) => validateFeedImportRecord({ previewRow, record })
  }).map((validationResult) => {
    const row = validationResult.record
    const dedupeResult = ensureUniqueImportedRowId({
      row,
      existingIds,
      importedIds,
      idPrefix: 'feed'
    })
    const dedupeIssue = createImportDedupeIssue({
      previewRow: validationResult.previewRow,
      targetField: 'id',
      dedupeResult
    })

    const normalizedRecordedAt = normalizeToIsoZ(trimOptionalString(row.recordedAt) ?? '')
    if (normalizedRecordedAt.ok) {
      row.recordedAt = normalizedRecordedAt.isoZ
    }

    const normalizedLot = resolveImportedFeedLot({
      row,
      availableLots: args.availableLots,
      lotKeyById: args.lotKeyById
    })
    // Charter D-1: FeedLogService rejects a create with no lotId. A row that
    // only resolved a free-text lot label (no matching Lot) would otherwise
    // save locally as "ready" and then fail permanently at sync time with no
    // surfaced error — flag it here instead, at import time.
    const lotIdIssue = !normalizedLot.lotId
      ? createImportRowIssue({
        previewRow: validationResult.previewRow,
        targetField: 'lotId',
        code: 'feed-lot-id-required',
        severity: 'error',
        message: normalizedLot.lotKey
          ? `Lot "${normalizedLot.lotKey}" did not match an existing lot. Select a matching lot before saving.`
          : 'A matching lot is required.'
      })
      : null
    const normalizedFeedKg = normalizeImportedFeedDecimalString(row.feedKg)
    const normalizedSfrPercent = normalizeImportedFeedDecimalString(row.sfrPercent)
    const normalizedBiomassKg = normalizeImportedFeedDecimalString(row.biomassKg)
    const normalizedFcr = normalizeImportedFeedDecimalString(row.fcr)
    const farmId = trimOptionalString(args.assignedContext?.farmId) ?? trimOptionalString(row.farmId) ?? ''
    const productionUnitId = trimOptionalString(args.assignedContext?.productionUnitId)
      ?? trimOptionalString(row.productionUnitId)
      ?? ''
    const feedType = trimOptionalString(row.feedType)
    const notes = trimOptionalString(row.notes)

    const typedRow: FeedLog = {
      id: toStableImportIdString(row.id),
      farmId,
      productionUnitId,
      recordedAt: typeof row.recordedAt === 'string' ? row.recordedAt : '',
      ...(normalizedLot.lotKey ? { lot: normalizedLot.lotKey } : {}),
      ...(normalizedLot.lotId ? { lotId: normalizedLot.lotId } : {}),
      ...(normalizedFeedKg ? { feedKg: normalizedFeedKg } : {}),
      ...(feedType ? { feedType } : {}),
      ...(normalizedSfrPercent ? { sfrPercent: normalizedSfrPercent } : {}),
      ...(normalizedBiomassKg ? { biomassKg: normalizedBiomassKg } : {}),
      ...(normalizedFcr ? { fcr: normalizedFcr } : {}),
      ...(notes ? { notes } : {})
    }

    // Preventive (lot, day) duplicate warning — same-batch or already-saved
    // collision. Skipped when lotId couldn't resolve (lotIdIssue already
    // covers that row) since `unknown-lot` would otherwise bucket every
    // unresolved row together.
    const lotToken = resolveCanonicalLotKey(typedRow.lotId, typedRow.lot)
    const dayKey = toDayKeyDateOnly(typedRow.recordedAt, dayKeyOptions)
    let dupIssue: ReturnType<typeof createImportRowIssue<FeedImportField>> | null = null
    if (lotToken !== 'unknown-lot' && dayKey) {
      const lotDayKey = `${dayKey}::${lotToken}`
      const collidesWithExisting = existingLotDayKeys.has(lotDayKey)
      const collidesWithImported = importedLotDayKeys.has(lotDayKey)
      if (collidesWithExisting || collidesWithImported) {
        dupIssue = createImportRowIssue({
          previewRow: validationResult.previewRow,
          targetField: 'recordedAt',
          code: 'feed-duplicate-lot-day',
          severity: 'warning',
          message: collidesWithExisting
            ? 'A feed log for this lot and day already exists. The import will create a duplicate — review before saving.'
            : 'Another imported row already targets this lot and day. The import will create a duplicate — review before saving.'
        })
      }
      importedLotDayKeys.add(lotDayKey)
    }

    const extraIssues = [dedupeIssue, lotIdIssue, dupIssue].filter((issue) => issue !== null)

    return {
      ...validationResult,
      record: typedRow,
      issues: extraIssues.length > 0 ? [...validationResult.issues, ...extraIssues] : validationResult.issues,
      dedupeResult
    }
  })

  return {
    table: parsedTable,
    mappedHeaders,
    results
  }
}

export const buildImportedFeedLogsFromCsvText = (args: {
  text: string
  existingRows: FeedLog[]
  availableLots: FeedImportLot[]
  lotKeyById: Map<string, string>
  assignedContext?: FeedImportContext | null
  timeZone?: string
}): FeedLog[] => {
  return buildImportedFeedLogResultsFromCsvText(args).map((result) => result.record)
}

export const buildImportedFeedLogResultsFromCsvText = (args: {
  text: string
  existingRows: FeedLog[]
  availableLots: FeedImportLot[]
  lotKeyById: Map<string, string>
  assignedContext?: FeedImportContext | null
  timeZone?: string
}): FeedImportRowResult[] => {
  return buildFeedImportArtifactsFromCsvText(args)?.results ?? []
}

export const buildFeedImportSessionFromCsvText = (args: {
  text: string
  existingRows: FeedLog[]
  availableLots: FeedImportLot[]
  lotKeyById: Map<string, string>
  sourceFile?: ImportSourceFile | null
  assignedContext?: FeedImportContext | null
  targetLabel?: string
  timeZone?: string
}): FeedImportSession | null => {
  const artifacts = buildFeedImportArtifactsFromCsvText(args)
  if (!artifacts) return null

  return buildImportSession({
    sourceFile: args.sourceFile ?? null,
    table: artifacts.table,
    selectedTarget: {
      targetKey: 'feed',
      label: args.targetLabel ?? 'Feed'
    },
    assignedContext: args.assignedContext ?? null,
    mappedHeaders: artifacts.mappedHeaders,
    results: artifacts.results
  })
}
